import React, { useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const ImageModal = ({ isOpen, onClose, imageUrl, imageAlt = "Image" }) => {
  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle image download
  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from imageAlt or use a default name
      const filename = imageAlt.includes('Generated image:') 
        ? `generated-image-${Date.now()}.png`
        : imageAlt.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.png';
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop with blur effect */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Action buttons */}
      <div className="absolute top-4 right-4 z-[10000] flex gap-2">
        {/* Download button */}
        <button
          onClick={handleDownload}
          className="p-3 text-white hover:text-gray-300 transition-all duration-200 bg-black/60 hover:bg-black/80 rounded-full backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg"
          title="Download image"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button>
      
        {/* Close button */}
        <button
          onClick={onClose}
          className="p-3 text-white hover:text-gray-300 transition-all duration-200 bg-black/60 hover:bg-black/80 rounded-full backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg"
          title="Close (Esc)"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      
      {/* Image container with improved sizing */}
      <div className="relative w-full h-full flex items-center justify-center p-16">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="max-w-[70vw] max-h-[70vh] object-contain image-modal-img-squircle shadow-2xl"
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
        />
      </div>
    </div>
  );
};

export default ImageModal; 